/**
 * AI Evaluation Assistant Component
 *
 * Allows managers to analyze conversation notes/transcriptions
 * and get AI-suggested scores and toelichtingen.
 */

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import type { AIAnalysisResult } from '@functieloonhuis/shared';
import { useAIAnalysis } from './useAIAnalysis';
import { AIResultsModal } from './AIResultsModal';

interface AIEvaluationAssistantProps {
  evaluationId: number;
  onApplied: () => void;
}

export function AIEvaluationAssistant({ evaluationId, onApplied }: AIEvaluationAssistantProps) {
  const [input, setInput] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AIAnalysisResult | null>(null);

  const analyzeMutation = useAIAnalysis(evaluationId);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Supported formats (for now: only plain text)
    const supportedTypes = ['text/plain', 'text/csv'];

    if (!supportedTypes.includes(file.type)) {
      toast.error('Alleen .txt en .csv bestanden worden ondersteund');
      return;
    }

    try {
      const text = await file.text();
      setInput(text);
      toast.success(`Bestand "${file.name}" geladen`);
    } catch {
      toast.error('Kon bestand niet laden');
    }
  };

  const handleAnalyze = () => {
    if (!input.trim()) {
      toast.error('Voer eerst notities of een transcriptie in');
      return;
    }

    if (input.trim().length < 50) {
      toast.error('Voer meer tekst in voor een goede analyse (minimaal 50 karakters)');
      return;
    }

    analyzeMutation.mutate(input, {
      onSuccess: (result) => {
        setAnalysisResult(result);
        setShowResults(true);
      },
      onError: (error: Error) => {
        toast.error(error.message || 'AI analyse mislukt. Probeer het opnieuw.');
      },
    });
  };

  return (
    <>
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-purple-200 bg-white/50">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900">AI Beoordelingsassistent</h3>
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
              Beta
            </span>
          </div>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Plak je gespreksnotities of transcriptie hieronder. De AI analyseert de tekst en
            genereert suggesties voor scores en toelichtingen.
          </p>

          <div>
            <textarea
              placeholder="Plak hier je notities of transcriptie van het beoordelingsgesprek...

Voorbeeld:
- Bespreking over projectwerk: Medewerker heeft uitstekend gewerkt aan het klantproject...
- Communicatie: Soms nog wat verbetering nodig in de schriftelijke communicatie...
- Teamwork: Goede samenwerking met collega's..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none bg-white"
            />
            <div className="mt-1 text-xs text-gray-500 text-right">
              {input.length} karakters
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleAnalyze}
              disabled={analyzeMutation.isPending || !input.trim()}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {analyzeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyseren...
                </>
              ) : (
                <>
                  <svg
                    className="mr-2 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Analyseren
                </>
              )}
            </button>

            <div className="relative">
              <input
                type="file"
                accept=".txt,.csv"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="mr-2 h-4 w-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                Bestand uploaden
              </button>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            Ondersteunde formaten: .txt, .csv (later: .pdf, .docx)
          </p>
        </div>
      </div>

      {analysisResult && (
        <AIResultsModal
          open={showResults}
          onClose={() => setShowResults(false)}
          result={analysisResult}
          evaluationId={evaluationId}
          onApplied={() => {
            setShowResults(false);
            setInput('');
            onApplied();
          }}
        />
      )}
    </>
  );
}
